import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cx from 'classnames'
import { motion } from 'framer-motion';
import { ParallaxProvider } from 'react-scroll-parallax';

import { BackgroundViewComponent } from '../background';
import { useDispatch, useSelector } from 'react-redux';
import { SLIDE } from '../../../constants/widgetTypes';
import { updateHyperLinkSlide } from '../../../actions/spatial';
import { WidgetsContext } from '../../../index';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import isEqual from 'lodash/isEqual';
import { dynamicImport } from '../../../services/dynamicImport';
import { FlipbookFlipContainer } from './flipBookFlipContainer';
import styles from './flipBook.module.scss';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip';

const KeyManager = dynamicImport(() => import('./KeyManager'));

const DESKTOP_ASPECT_RATIO = 16 / 9;
const MOBILE_ASPECT_RATIO = 9 / 16;
const HIGHLIGHT_WIDTH = 225;
const PAGES_TO_LOAD = 10;
const PAGES_TO_RENDER = 5;
const SUB_SLIDE_TRANSITION_MS = 450; // must match .subSlideCard CSS transition (400ms + 50ms buffer)
const MOBILE_SUBSLIDE_STACK_MS = 400; // vertical stack translate — keep in sync with .subSlideVerticalTrack
const MENU_BAR_HEIGHT = 67;          // top bar height — keep in sync with .flipbookTopBar height in scss
const BOTTOM_HOVER_ZONE = 150;       // px from card bottom — reveal page indicator on hover

// ─── hooks ────────────────────────────────────────────────────────────────────

export const useDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(min-width: 992px)');
      setIsDesktop(mediaQuery.matches);
      const handler = () => setIsDesktop(mediaQuery.matches);
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, []);

  return mounted ? isDesktop : true;
};

export const useMobilePortrait = () => {
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobilePortrait(window.innerHeight > window.innerWidth);
      }
    };
    handleResize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  if (!mounted) return false;
  return isMobilePortrait;
};

// ─── FlipbookContent ──────────────────────────────────────────────────────────

export const FlipbookContent = React.memo(({
  breakpoint,
  initialPage,
  pages,
  appearance,
  onChange,
  children,
  onFetchPage,
  imageCDN,
  updatePagesToRender
}) => {
  const dispatch = useDispatch();
  const containerRef        = useRef(null);
  const flipbookPageRef     = useRef(null);
  const flipbookPageInnerRef= useRef(null);
  const isAnimatingRef      = useRef(false);   // blocks concurrent flips
  const pendingPageNavRef   = useRef(null);    // deferred nav after sub-slide close
  const navigateToPageRef   = useRef(() => Promise.resolve());
  // ─ horizontal swipe tracking (page-to-page, lives HERE not in FlipbookPage) ─
  const touchStartXRef      = useRef(0);
  const touchStartYRef      = useRef(0);       // used to ignore vertical swipes
  // ─ content snapshots — frozen at flip-start so re-renders don't corrupt slots ─
  const snapshotCurrentRef  = useRef(null);
  const snapshotUpcomingRef = useRef(null);
  // pending onChange payload — fired AFTER the animation completes
  const pendingOnChangeRef  = useRef(null);
  // registered by the active FlipbookPage so Menu arrows can delegate slide logic
  const slideNavigatorRef   = useRef({ next: null, prev: null });

  const singlePage    = pages?.length === 1;
  const mobilePotrait = useMobilePortrait();
  const viewportSize  = useViewportSize();
  const desktop       = useDesktop();

  const aspectRatio   = !mobilePotrait ? DESKTOP_ASPECT_RATIO : MOBILE_ASPECT_RATIO;
  // Slide CONTENT layout follows ORIENTATION (landscape → 'desktop' 16:9, portrait →
  // 'mobile' 9:16) — matching the card's aspect ratio. This is published as
  // context.breakpoint so the consumer's slide widgets render the right layout (e.g.
  // an iPad in landscape gets the desktop slide, not the mobile one). The flipbook's
  // INTERACTION (`breakpoint` prop → experienceBreakpoint) is kept separate below.
  const slideBreakpoint = mobilePotrait ? 'mobile' : 'desktop';
  // Mobile + iPad portrait: the menu bar moves OUT of the card to a fixed bar at
  // the viewport top, and the card gets bigger top/bottom (room for the bar) +
  // minimal left/right gutters. Landscape keeps the in-card bar.
  const externalBar   = breakpoint === 'mobile' || mobilePotrait;

  const [mounted, setMounted] = useState(false);

  const [state, setState] = useState({
    initialized:      false,
    dimensions:       { width: 0, height: 0 },
    viewportHeight:   '100vh',
    scale:            1,
    menuPosition:     { x: 0, y: 0 },
    showHighlight:    false,
    fullscreen:       false,
    subSlideOpen:     false,
    subSlideScrollDir: null,   // mobile: sticky last scroll direction — 'down' → small pill, 'up' → normal
    subSlideAtBottom: false,   // mobile: at the bottom of the sub-slide content → normal pill
    activePageHasSubSlides: false,
    menuPanelOpen:    false,   // Contents slide-in panel
    backgroundPosition: 0,
    mobilePotrait,
    activePage: {
      id:         initialPage,
      fullscreen: singlePage,
      slideIndex: 0,
    },
    // ── flip state ──
    pendingPageId:     null,
    flipDirection:     null,   // 'next' | 'prev' | null
    pendingSlideIndex: 0,
    gatingActive:      false,
  });

  // ── lifecycle ────────────────────────────────────────────────────────────────

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    setState(s => ({ ...s, mobilePotrait }));
  }, [mobilePotrait]);

  useEffect(() => {
    if (!mounted) return;
    const timer = setTimeout(() => {
      setState(s => ({ ...s, initialized: true }));
    }, 1000);
    onChange('index', { id: state.activePage.id, slideIndex: 0 });
    return () => clearTimeout(timer);
  }, [mounted]); // eslint-disable-line

  useEffect(() => {
    if (!initialPage) return;
    setState(s => ({ ...s, activePage: { ...s.activePage, id: initialPage } }));
  }, [initialPage]);

  useEffect(() => {
    if (!mounted) return;
    handleResize(state.showHighlight);
  }, [viewportSize, state.showHighlight, mounted]); // eslint-disable-line

  // menu position maths
  useEffect(() => {
    if (!mounted) return;
    const menuElement = document.getElementById('cs-flipbook-menu');
    if (state.dimensions.width === 0 || !flipbookPageInnerRef.current || !menuElement) return;

    const menuRect              = menuElement.getBoundingClientRect();
    const flipbookPageRect      = flipbookPageRef.current.getBoundingClientRect();
    const flipbookPageInnerRect = flipbookPageInnerRef.current.getBoundingClientRect();

    let menuX;
    let menuY;
    if (desktop) {
      const widthDiff            = flipbookPageRect.width - flipbookPageInnerRect.width;
      const flipbookPageCenterX  = state.showHighlight
        ? flipbookPageInnerRect.width / 2 + 50
        : flipbookPageInnerRect.width / 2 + widthDiff / 2;
      menuX = flipbookPageCenterX - menuRect.width / 2;
      menuY = state.fullscreen
        ? flipbookPageInnerRect.bottom - (menuRect.height + 20)
        : flipbookPageInnerRect.bottom - menuRect.height / 2;
    } else {
      // Phone + iPad portrait (<992px): center toolbar on the card container x-axis.
      // Card is now full-height, so anchor the pill 23px ABOVE the viewport bottom
      // (the old +20 pushed it below the screen once the card filled the viewport).
      // Anchor the pill by its BOTTOM-CENTER (see translate(-50%,-100%) on the Menu) so
      // resizing it (compact ↔ full) never drifts its position.
      menuX = flipbookPageRect.left + flipbookPageRect.width / 2;
      menuY = flipbookPageInnerRect.bottom - 23;
    }

    setState(s => ({
      ...s,
      menuPosition: { x: menuX, y: menuY },
      highlight: { ...s.highlight, x: flipbookPageInnerRect.width + 80, y: flipbookPageInnerRect.top },
    }));
  }, [state.dimensions, state.initialized, mounted, desktop, state.fullscreen, state.showHighlight]); // eslint-disable-line


  useEffect(() => {
    if (state.subSlideOpen || !pendingPageNavRef.current) return;
    const nav = pendingPageNavRef.current;
    pendingPageNavRef.current = null;

    // Mobile: unchanged — single delay then page flip.
    // Desktop/tablet: wait for sub-slide card to slide out (cover stays static underneath), then flip.
    if (nav.desktopCrossPageChoreography) {
      const delay = SUB_SLIDE_TRANSITION_MS;
      const timer = setTimeout(() => {
        isAnimatingRef.current = false;
        navigateToPageRef.current(nav.id, nav.fullscreen, nav.slideIndex, nav.isManual);
      }, delay);
      return () => clearTimeout(timer);
    }

    const delay = nav.closeDelayMs ?? SUB_SLIDE_TRANSITION_MS;
    const timer = setTimeout(() => {
      isAnimatingRef.current = false;
      navigateToPageRef.current(nav.id, nav.fullscreen, nav.slideIndex, nav.isManual);
    }, delay);
    return () => clearTimeout(timer);
  }, [state.subSlideOpen]); // eslint-disable-line

  // side-effects that depend on the active page
  useEffect(() => {
    if (!mounted) return;
    calculateBackgroundPosition();
    handleLazyLoadPages();
    handleUpdatePagesToRender();
  }, [state.activePage.id, state.initialized, mounted]); // eslint-disable-line

  // ── helpers ──────────────────────────────────────────────────────────────────

  const handleLazyLoadPages = () => {
    const activeIndex = pages.findIndex(obj => obj.id === state.activePage.id);
    if (activeIndex < 0) return;
    let closestIndices = getClosestIndices(pages, activeIndex, PAGES_TO_LOAD);
    closestIndices = [...closestIndices, activeIndex];
    closestIndices.map(i => pages[i]).forEach(page => {
      if (page.hidden) return;
      if (page.loading || page.loaded) return;
      if (onFetchPage) onFetchPage(page.id);
    });
  };

  const handleUpdatePagesToRender = () => {
    const activeIndex    = pages.findIndex(obj => obj.id === state.activePage.id);
    const closestIndices = getClosestIndices(pages, activeIndex, PAGES_TO_RENDER);
    const closestIds     = closestIndices.map(i => pages[i]).filter(p => !p.hidden).map(p => p.id);
    const unique         = uniq([...closestIds, state.activePage.id]);
    if (updatePagesToRender) updatePagesToRender(unique, state.activePage.id);
  };

  const getClosestIndices = (pages, index, limit) => {
    const result = [];
    const len    = pages.length;
    if (len <= limit) return pages.map((_, i) => i);
    for (let i = index - 1; i >= 0 && result.length < limit / 2; i--)  result.unshift(i);
    for (let i = index + 1; i < len  && result.length < limit;     i++) result.push(i);
    for (let i = index - 2; i >= 0   && result.length < limit;     i--) result.unshift(i);
    return result;
  };

  const handleResize = useCallback((showHighlight) => {
    if (!mounted || !viewportSize || viewportSize.width === 0) return;
    const { width, height, viewportHeight } = viewportSize;

    // Gutters keep the card off the viewport edges (card stays aspect-fit + centered).
    //  • Landscape: wider left/right than top/bottom.
    //  • Mobile / iPad portrait: NO gutters — the card is full-bleed (fills the whole
    //    viewport; see the full-screen override below).
    const gutterX = externalBar ? 0 : 100;   // left/right
    const gutterY = externalBar ? 0 : 50;    // top/bottom

    const calculateAvailableWidth = () => {
      return showHighlight ? width - ((HIGHLIGHT_WIDTH * state.scale) + 120) : width - gutterX * 2;
    };

    const availableWidth  = calculateAvailableWidth();
    const availableHeight = showHighlight ? height : height - gutterY * 2;

    let cardWidth, cardHeight, scale;

    if (externalBar) {
      // MOBILE / iPad portrait — full-bleed card; the menu bar is external so the whole
      // card height feeds the aspect-fit scale and the slide fills the viewport width.
      let newHeightWidth = availableWidth / aspectRatio;
      if (newHeightWidth > availableHeight) newHeightWidth = availableHeight;
      scale      = newHeightWidth === 1024 ? newHeightWidth / 500 : newHeightWidth / 675;
      cardWidth  = width;
      cardHeight = height;
    } else {
      // DESKTOP / iPad landscape — the slide's CONTENT AREA (below the in-card bar) must
      // be 16:9, matching the 1200×675 editor canvas, so the slide is flush to the card
      // WIDTH (no side gaps) and inner widgets align to the edges. The bar is ADDED on
      // top → cardHeight = contentAreaHeight + MENU_BAR_HEIGHT, and scale = contentH / 675
      // fills the card width exactly.
      // (Previously the CARD was 16:9 and the bar was subtracted from the content height,
      // which scaled the slide narrower than the card and misaligned the widgets.)
      let contentW = availableWidth;
      let contentH = contentW / aspectRatio;
      let cardH    = contentH + MENU_BAR_HEIGHT;
      if (cardH > availableHeight) {
        cardH    = availableHeight;
        contentH = Math.max(cardH - MENU_BAR_HEIGHT, 0);
        contentW = contentH * aspectRatio;
      }
      scale      = contentH / 675;
      cardWidth  = contentW;
      cardHeight = cardH;
    }

    setState(s => ({
      ...s,
      scale,
      showHighlight,
      viewportHeight,
      dimensions: { width: cardWidth, height: cardHeight },
    }));
  }, [viewportSize, state.showHighlight, mounted]); // eslint-disable-line

  const calculateBackgroundPosition = () => {
    if (!mounted) return;
    const backgroundImage = document.getElementById('cs-flipbook-bg');
    if (!backgroundImage) return;
    const backgroundImageRect = backgroundImage.getBoundingClientRect();
    const containerRect       = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    const activeIndex = pages.findIndex(obj => obj.id === state.activePage.id);
    const position = (backgroundImageRect.width - containerRect.width) / pages?.length * activeIndex;
    setState(s => ({ ...s, backgroundPosition: position }));
  };

  const setSubSlideOpenCtx = (open) => {
    // Closing sub-slides resets the scroll-driven pill state.
    setState(s => ({ ...s, subSlideOpen: open, ...(open ? {} : { subSlideScrollDir: null, subSlideAtBottom: false }) }));
  };

  const setSubSlideScrollCtx = ({ dir, atBottom }) => {
    setState(s => {
      const nextDir = dir === undefined ? s.subSlideScrollDir : dir;
      const nextAtBottom = atBottom === undefined ? s.subSlideAtBottom : !!atBottom;
      if (s.subSlideScrollDir === nextDir && s.subSlideAtBottom === nextAtBottom) return s;
      return { ...s, subSlideScrollDir: nextDir, subSlideAtBottom: nextAtBottom };
    });
  };

  const setMenuPanelOpenCtx = (open) => {
    setState(s => (s.menuPanelOpen === !!open ? s : { ...s, menuPanelOpen: !!open }));
  };

  const handleToggleFullscreen = () => {
    const isFs = document.fullscreenElement || document.webkitFullscreenElement
      || document.mozFullScreenElement || document.msFullscreenElement;

    if (isFs) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    } else {
      const el = containerRef.current || document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen
        || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (req) req.call(el);
    }
  };

  // ── navigateToPage (flip trigger) ────────────────────────────────────────────

  const navigateToPage = (id, fullscreen, slideIndex = 0, isManual = false, navOptions = {}) => {
    const currentId = state.activePage.id;

    if (isAnimatingRef.current) return Promise.resolve();

    // ── If sub-slides are open, close them first then navigate ──────────────
    // Store the target, close sub-slides, block navigation.
    // A useEffect watching state.subSlideOpen will pick up the pending nav
    // after the close animation finishes.
    if (state.subSlideOpen && id !== currentId) {
      const isMobileBp = breakpoint === 'mobile';
      pendingPageNavRef.current = {
        id,
        fullscreen,
        slideIndex,
        isManual,
        closeDelayMs: navOptions.closeDelayMs ?? SUB_SLIDE_TRANSITION_MS,
        desktopCrossPageChoreography: !isMobileBp,
      };
      isAnimatingRef.current = true;
      setState(s => ({ ...s, subSlideOpen: false }));
      return Promise.resolve();
    }

     if (id === currentId) {
       setState(s => ({
         ...s,
         activePage: { ...s.activePage, slideIndex: slideIndex ?? 0 },
       }));
       return Promise.resolve();
    }

    const childrenArray = React.Children.toArray(children);
    const activeIndex   = pages.findIndex(p => p.id === currentId);
    const targetIndex   = pages.findIndex(p => p.id === id);
    if (targetIndex < 0)        return Promise.resolve();

    const direction = targetIndex > activeIndex ? 'next' : 'prev';

    if (isManual) {
      dispatch(updateHyperLinkSlide({ page: '', slide: '', uid: '' }));
    }

    pendingOnChangeRef.current = { id, slideIndex };
    isAnimatingRef.current = true;

    // Pre-render the upcoming page's widgets synchronously so they are mounted
    // before the animation slots capture their snapshots. Without this, the rAF
    // call happened AFTER setState(flipDirection), causing widgets to mount
    // inside the animation slots mid-flip and producing a visible glitch.
    if (updatePagesToRender) {
      updatePagesToRender([currentId, id], currentId);
    }

    // Defer snapshot capture + animation start to the next frame so React can
    // commit the pre-render above first. The snapshots then capture fully-mounted
    // widget trees instead of empty stubs.
    requestAnimationFrame(() => {
      const freshChildren = React.Children.toArray(children);
      snapshotCurrentRef.current  = freshChildren[activeIndex]  ?? null;
      snapshotUpcomingRef.current = freshChildren[targetIndex]  ?? null;

      setState(s => ({
        ...s,
        pendingPageId:     id,
        flipDirection:     direction,
        pendingSlideIndex: slideIndex,
      }));
    });

    return Promise.resolve();
  };

  navigateToPageRef.current = navigateToPage;

  // ── handleFlipComplete — called by FlipbookFlipContainer timer ───────────────

  const handleFlipComplete = useCallback(() => {
    setState(s => {
      // Fire deferred onChange only for page-to-page flips
      if (pendingOnChangeRef.current && s.pendingPageId !== s.activePage.id) {
        onChange('index', pendingOnChangeRef.current);
        pendingOnChangeRef.current = null;
      }

      // Clear snapshots — live children take over again
      snapshotCurrentRef.current  = null;
      snapshotUpcomingRef.current = null;

      isAnimatingRef.current = false;

      return {
        ...s,
        activePage: {
          ...s.activePage,
          id:         s.pendingPageId,
          slideIndex: s.pendingSlideIndex ?? 0,
        },
        pendingPageId:     null,
        flipDirection:     null,
        pendingSlideIndex: 0,
      };
    });
  }, [onChange]);

  // ── Horizontal swipe handlers (page-to-page navigation, desktop carousel) ────
  // These live on the outer <motion.div> carousel and handle LEFT/RIGHT swipes.
  // Vertical touch events are intentionally ignored here; FlipbookPage handles
  // UP/DOWN for slide scrolling.

  const handleCarouselTouchStart = useCallback((e) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  }, []);

  const handleCarouselTouchEnd = useCallback((e) => {
    if (state.subSlideOpen) return; // sub-slide card handles its own swipe

    const deltaX = touchStartXRef.current - e.changedTouches[0].clientX;
    const deltaY = touchStartYRef.current - e.changedTouches[0].clientY;

    // Ignore if predominantly vertical (user is scrolling slides, not pages)
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;

    const threshold   = 50;
    if (Math.abs(deltaX) < threshold) return;

    const activeIndex = pages.findIndex(p => p.id === state.activePage.id);

    if (deltaX > 0 && activeIndex < pages.length - 1) {
      navigateToPage(pages[activeIndex + 1].id, state.fullscreen, 0, true);
    } else if (deltaX < 0 && activeIndex > 0) {
      navigateToPage(pages[activeIndex - 1].id, state.fullscreen, 0, true);
    }
  }, [state.activePage.id, state.fullscreen, state.subSlideOpen, pages]); // eslint-disable-line

  // ── SSR guard ────────────────────────────────────────────────────────────────

  if (!mounted) {
    return (
      <div style={{ height: '100vh' }} className={cx(styles.carousel)}>
        <div className={styles.loading}>
          <div className={styles.loader}>
            <video loop autoPlay playsInline muted>
              <source src='/images/shortLoader.mp4' type='video/mp4' />
            </video>
            <label>Loading Flipbook content...</label>
          </div>
        </div>
      </div>
    );
  }

  // ── resolve current + upcoming children ──────────────────────────────────────
  // During a flip: use the snapshots frozen at navigate-time (immune to re-renders).
  // When idle:     use live children so the active page always reflects latest data.

  const childrenArray = React.Children.toArray(children);
  const activeIndex   = pages.findIndex(p => p.id === state.activePage.id);

  // Mobile / iPad-portrait header pill label = current page name (fallback "Content").
  const activePageName = (pages || []).find(p => p.id === state.activePage.id)?.name || 'Content';

  const isFlipping = !!state.flipDirection;

  const currentContent = isFlipping
    ? snapshotCurrentRef.current                   // frozen snapshot
    : (childrenArray[activeIndex] ?? null);        // live child

  const upcomingContent = isFlipping
    ? snapshotUpcomingRef.current                  // frozen snapshot
    : null;                                        // nothing to show when idle

  // ── render ───────────────────────────────────────────────────────────────────

  return (
    <FlipbookContext.Provider value={{
      ...state,
      breakpoint: slideBreakpoint,      // CONTENT/orientation — consumer slides read this for layout
      experienceBreakpoint: breakpoint, // INTERACTION/device — flipbook reads this for flip/sub-slide/bar
      pages,
      appearance,
      navigateToPage,
      setSubSlideOpen: setSubSlideOpenCtx,
      setSubSlideScroll: setSubSlideScrollCtx,
      setMenuPanelOpen: setMenuPanelOpenCtx,
      onToggleFullscreen: handleToggleFullscreen,
      onChange,
      // ── slide-aware navigation (registered by FlipbookPage) ──────────────────
      registerSlideNavigator:  (nav) => {
        slideNavigatorRef.current = nav;
        const has = !!(nav && nav.hasSubSlides);
        setState(s => (s.activePageHasSubSlides === has ? s : { ...s, activePageHasSubSlides: has }));
      },
      navigateNextAction:      ()    => { slideNavigatorRef.current?.next?.(); },
      navigatePrevAction:      ()    => { slideNavigatorRef.current?.prev?.(); },
      toggleSubSlidesAction:   ()    => { slideNavigatorRef.current?.toggleSubSlides?.(); },
      setGatingActive:         (active) => setState(s => {
        const next = !!active;
        if (s.gatingActive === next) return s;
        // When gating appears, also close the Contents panel (and its scrim).
        return { ...s, gatingActive: next, ...(next ? { menuPanelOpen: false } : {}) };
      }),
    }}>
      <Suspense fallback={null}>
        <KeyManager>
          <motion.div
            key={breakpoint}
            ref={containerRef}
            style={{ height: state.viewportHeight }}
            className={cx(styles.carousel)}
            {...(breakpoint === 'mobile' ? {
              onTouchStart: handleCarouselTouchStart,
              onTouchEnd:   handleCarouselTouchEnd,
            } : {})}
          >
            {!state.initialized && (
              <div className={styles.loading}>
                <div className={styles.loader}>
                  <video loop autoPlay playsInline muted>
                    <source src='/images/shortLoader.mp4' type='video/mp4' />
                  </video>
                  <label>Loading Flipbook content...</label>
                </div>
              </div>
            )}

            {/* Bottom nav — mobile + iPad portrait only (external-bar layouts). */}
            {externalBar && (
              <Menu
                menuPosition={state.menuPosition}
                activePage={state.activePage}
                navigateToPage={navigateToPage}
                pages={pages}
              />
            )}

            <ParallaxProvider>
              {/* Invisible placeholder for menu position maths */}
              <div
                className={cx(styles.flipbookPage, styles.flipbookPagePlaceholder)}
                ref={flipbookPageRef}
              >
                <div
                  className={styles.flipbookPagePlaceholderInner}
                  ref={flipbookPageInnerRef}
                  style={{
                    width:           state.dimensions.width,
                    height:          state.dimensions.height,
                    transformOrigin: 'center top',
                  }}
                />
              </div>

              <FlipbookFlipContainer
                currentContent={currentContent}
                upcomingContent={upcomingContent}
                direction={state.flipDirection}
                onAnimationEnd={handleFlipComplete}
                mobile={breakpoint === 'mobile'}
              />
            </ParallaxProvider>

            <ContentsPanel
              open={state.menuPanelOpen}
              pages={pages}
              activePageId={state.activePage.id}
              imageCDN={imageCDN}
              dimensions={state.dimensions}
              breakpoint={breakpoint}
              externalBar={externalBar}
              onSelect={(id) => {
                setMenuPanelOpenCtx(false);
                navigateToPage(id, state.fullscreen, 0, true);
              }}
              onClose={() => setMenuPanelOpenCtx(false)}
            />

            {/* Mobile / iPad portrait: single menu bar at the viewport top, OUTSIDE the
                card (rendered at the carousel level). Hamburger opens Contents; Expand toggles fullscreen. */}
            {externalBar && (
              <div className={styles.flipbookMobileBar}>
                <button
                  className={styles.menuButton}
                  type="button"
                  aria-label="Menu"
                  disabled={!!state.gatingActive}
                  onClick={() => setMenuPanelOpenCtx(!state.menuPanelOpen)}
                >
                  <i className="cleverstory-flip-menu" />
                  <span className={styles.title}>{activePageName}</span>
                </button>
                <button
                  className={styles.menuButton}
                  type="button"
                  style={{visibility: breakpoint === 'mobile' ? 'hidden' : 'visible'}}
                  aria-label="Expand"
                  onClick={() => handleToggleFullscreen()}
                >
                  <i className="cleverstory-expand-editor" />
                </button>
              </div>
            )}

          </motion.div>
        </KeyManager>
      </Suspense>
    </FlipbookContext.Provider>
  );
});

// ─── ContentsPanel (left slide-in, "Contents") ────────────────────────────────
// Single instance rendered at FlipbookContent level (NOT inside FlipbookPage), so
// it does not duplicate/fold during a flip. Anchored to the card's inner-left edge
// via CSS vars; mobile overrides to full-screen in scss.
const ContentsPanel = React.memo(({ open, pages, activePageId, imageCDN, dimensions, breakpoint, externalBar, onSelect, onClose }) => {
  const list = (pages || []).filter(p => !p.hidden);

  const thumbSrc = (page) => {
    const iconUrl = get(page, 'icon.url');
    if (!iconUrl) return null;
    return `${imageCDN}/${iconUrl}`;
  };

  return (
    <>
      {/* click-outside scrim (desktop) */}
      <div
        className={cx(styles.contentsScrim, { [styles.contentsScrimOpen]: open, [styles.contentsScrimFocus]: externalBar })}
        onClick={onClose}
      />
      {/* clip container aligned to the card box — clips the panel at the card's
          left edge so it slides in from INSIDE the card, not from outside.
          External-bar layouts (phones + iPad portrait) drop the card-box clip and
          slide the drawer in from the VIEWPORT edge instead — driven by the
          orientation-aware `externalBar` flag, NOT a width media query, so iPad
          portrait (≥768px wide) gets the mobile drawer too. */}
      <div
        className={cx(styles.contentsClip, { [styles.contentsClipExternal]: externalBar })}
        style={{
          '--panel-left': `calc((100% - ${dimensions.width}px) / 2)`,
          '--panel-top':  `calc((100% - ${dimensions.height}px) / 2)`,
          '--panel-h':    `${dimensions.height}px`,
        }}
      >
      <div
        className={cx(styles.contentsPanel, { [styles.contentsPanelOpen]: open, [styles.contentsPanelExternal]: externalBar })}
        role="dialog"
        aria-label="Contents"
      >
        {/* top toggle / close */}
        <div className={cx(styles.contentsHeader, { [styles.contentsHeaderExternal]: externalBar })}>
          {externalBar ? (
            <button className={styles.contentsMenuToggle} type="button" aria-label="Close contents" onClick={onClose}>
              <i className="cleverstory-flip-menu" />
              <span className={styles.contentsMenuLabel}>Contents</span>
            </button>
          ) : (
            <>
              <h2 className={styles.contentsTitle}>Contents</h2>
              <button className={styles.contentsClose} type="button" aria-label="Close" onClick={onClose}>
                <i className="cleverstory-close" />
              </button>
            </>
          )}
        </div>

        <div className={styles.contentsList}>
          {list.map((page, index) => {
            const src = thumbSrc(page);
            return (
              <div
                key={page.id}
                className={cx(styles.contentsCard, { [styles.contentsCardActive]: page.id === activePageId })}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(page.id)}
              >
                <div className={styles.contentsCardPreview}>
                  {src
                    ? <img src={src} alt="" loading="lazy" />
                    : <div className={styles.contentsCardPreviewEmpty} />}
                </div>
                <div className={styles.contentsCardName} title={page.name}>{page.name}</div>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </>
  );
});

// ─── FlipbookContext ──────────────────────────────────────────────────────────

export const FlipbookContext = React.createContext({
  showHighlight: false,
  fullscreen: false,
  subSlideOpen: false,
  activePageHasSubSlides: false,
  dimensions: { width: 0, height: 0 },
});

FlipbookContent.FlipbookContext = FlipbookContent;

// ─── Per-slide card background (`properties.{breakpoint}.background` on SLIDE) ─
// When a slide clears its background (`null`), the card must stay opaque — otherwise
// fold/slide animations show the next page through a transparent stack.

const FLIPBOOK_EMPTY_SLIDE_BACKGROUND = Object.freeze({
  type:  'empty',
  color: { type: 'solid', r: 255, g: 255, b: 255, a: 1 },
});

function isRenderableSlideBackground(bg) {
  return bg != null && typeof bg === 'object' && get(bg, 'type') != null;
}

/**
 * Resolves slide card background for flip animations. Flipbook slide backgrounds are
 * stored on `properties.desktop.background` for every viewport (mobile/tablet/desktop).
 * If the slide explicitly clears background (`null`), use an opaque white fill so folds
 * do not show the next layer through a transparent stack.
 * @param {string} [_breakpoint] retained for callers; not used for property lookup
 */
function resolveSlideBackgroundFromElement(slideEl, appearanceFallback, _breakpoint) {
  const wp = get(slideEl, 'props.widgetProps');
  const bg = wp ? get(wp, 'properties.desktop.background') : undefined;

  if (bg === null) {
    return FLIPBOOK_EMPTY_SLIDE_BACKGROUND;
  }
  if (isRenderableSlideBackground(bg)) {
    return bg;
  }
  if (isRenderableSlideBackground(appearanceFallback)) {
    return appearanceFallback;
  }
  return FLIPBOOK_EMPTY_SLIDE_BACKGROUND;
}

// ─── FlipbookPage ─────────────────────────────────────────────────────────────

export const FlipbookPage = React.memo(({ appearance, page, children, slideWidgets = [], loading = null }) => {

  const dispatch         = useDispatch();
  const flipbookPageRef  = useRef(null);
  const flipbookContext  = useContext_safe(FlipbookContext);
  const defaultSlideBg   = get(appearance, 'slideBackground');
  const breakpoint       = flipbookContext.breakpoint;            // CONTENT/orientation — for slide backgrounds
  const experienceBreakpoint = flipbookContext.experienceBreakpoint ?? breakpoint; // INTERACTION/device
  // Mobile + iPad portrait: bar is external (portaled to viewport top) → no in-card
  // bar, so the in-card bar offsets (cover content, sub-slide card, fold overlay) are
  // disabled and content uses the full card height.
  const externalBar      = experienceBreakpoint === 'mobile' || flipbookContext.mobilePotrait;
  const widgetContext    = React.useContext(WidgetsContext);

  // ── Sub-slide overlay (state lives in context so snapshots inherit it) ─────
  const subSlideOpen    = flipbookContext.subSlideOpen;
  const subSlideOpenRef = useRef(false);
  useEffect(() => { subSlideOpenRef.current = subSlideOpen; }, [subSlideOpen]);
  const setSubSlideOpen = flipbookContext.setSubSlideOpen || (() => {});
  const childrenArray   = React.Children.toArray(children);
  const coverSlide      = childrenArray[0] || null;
  const subSlides       = useMemo(() => childrenArray.slice(1), [childrenArray.length]); // eslint-disable-line
  const hasSubSlides    = subSlides.length > 0 || slideWidgets.length > 1;

  const coverBackground = useMemo(
    () => resolveSlideBackgroundFromElement(coverSlide, defaultSlideBg, breakpoint),
    [coverSlide, defaultSlideBg, breakpoint],
  );
  // ── Sub-slide flip state (self-contained within this component) ─────────────
  const currentSubSlideRef    = useRef(0); // 0-based index within subSlides array
  const isSubFlipAnimatingRef = useRef(false);
  const [subSlideFlip, setSubSlideFlip] = useState({
    currentIndex: 0,      // which sub-slide is currently shown
    direction:    null,    // 'next' | 'prev' | null
    targetIndex:  null,    // which sub-slide to flip to
  });
  // Mobile (breakpoint === 'mobile'): vertical stack index — desktop uses subSlideFlip + fold only
  const [mobileSubSlideIndex, setMobileSubSlideIndex] = useState(0);

  // ── Hover-reveal nav arrows: show only the arrow for the half the cursor is in ──
  // ('left' | 'right' | null). Desktop only in effect (arrows are display:none on
  // mobile/tablet-portrait). Separate state for cover vs sub-slide to avoid cross-talk.
  const [coverArrowSide, setCoverArrowSide] = useState(null);
  const [coverBottomHover, setCoverBottomHover] = useState(false);
  const [subArrowSide,   setSubArrowSide]   = useState(null);

  const idleSubSlideBackground = useMemo(
    () => resolveSlideBackgroundFromElement(
      subSlides[subSlideFlip.currentIndex] ?? null,
      defaultSlideBg,
      breakpoint,
    ),
    [subSlides, subSlideFlip.currentIndex, defaultSlideBg, breakpoint],
  );

  // ── refs that track mutable values without causing re-renders ────────────────
  // Global index into slideWidgets: 0 = cover, 1+ = sub-slides (same model as SpatialPage.currentSlideIndexRef)
  const currentSlideIndexRef = useRef(
    flipbookContext.activePage?.id === page?.id
      ? (flipbookContext.activePage?.slideIndex ?? 0)
      : 0
  );
  // startYRef tracks the Y position for VERTICAL slide scrolling (NOT horizontal page swipe)
  const startYRef        = useRef(0);
  const fullscreenRef    = useRef(flipbookContext.fullscreen);
  const activePageRef    = useRef(flipbookContext.activePage);
  const slideWidgetsRef  = useRef(slideWidgets);
  const prevActiveIdRef  = useRef(flipbookContext.activePage?.id === page?.id ? null : flipbookContext.activePage?.id);
  // Always-current flipbookContext for use inside event listeners (which have stale closures)
  const flipbookContextRef = useRef(flipbookContext);

  const slideDetails = useSelector(state => get(state, 'pages.spatial.hyperlinkSlide', {}));
  const pages        = useSelector(state => get(state, 'pages.spatial.items', []));
  const activePageId = activePageRef.current?.id || '';
  const carouselSlide= get(widgetContext, 'carouselSlide', null) || null;

  const url = useMemo(() => {
    return pages.find(obj => obj.id === slideDetails.page)?.canonical || '';
  }, [slideDetails.page, pages]);

  const editorWidgets = useSelector(
    state => {
      const fromEditors = get(state, `editors.${url}.widgets`);
      if (fromEditors) return get(state, `editors.${url}.widgets`, []);
      return get(state, `${widgetContext?.selector}.widgets`) || [];
    },
    isEqual,
  );

  const slideMemoIndex = useMemo(() => {
    try {
      const uid = (editorWidgets || []).find(obj => obj.uid === slideDetails?.uid)?.uid;
      if (uid) {
        const sorted = (editorWidgets || []).filter(d => d.type === SLIDE).sort((a, b) => a.order - b.order);
        return sorted.findIndex(obj => obj.uid === uid);
      }
      return -1;
    } catch { return -1; }
  }, [JSON.stringify(editorWidgets), slideDetails?.uid]);

  const widgetSlideIndex = useMemo(() => {
    try {
      return (slideWidgets || []).findIndex(obj => obj.uid === slideDetails?.uid);
    } catch { return -1; }
  }, [JSON.stringify(slideWidgets), slideDetails?.uid]);

  // sync refs
  useEffect(() => { slideWidgetsRef.current = slideWidgets; }, [slideWidgets]);
  // keep flipbookContextRef always pointing at the latest context value
  flipbookContextRef.current = flipbookContext;

  const syncActiveSlideIndex = useCallback((globalIndex) => {
    currentSlideIndexRef.current = globalIndex;
    const ctx = flipbookContextRef.current;
    if (!page?.id || ctx.activePage?.id !== page?.id) return;
    const prev = ctx.activePage?.slideIndex ?? 0;
    if (prev !== globalIndex) {
      ctx.navigateToPage(page.id, fullscreenRef.current, globalIndex);
    }
  }, [page?.id]);

  // ── Sub-slide flip content resolution ───────────────────────────────────────
  // Mirrors the page-level pattern: each sub-slide is wrapped as a card-sized
  // element centered in the full viewport, so the fold animation shows the card
  // edge rotating away with environment behind it — matching page-level flip.
  const subSlideOuterStyle = useMemo(() => ({
    position: 'relative', width: '100%', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }), []);
  const subSlideCardStyle = useMemo(() => ({
    width:  flipbookContext.dimensions.width,
    height: flipbookContext.dimensions.height,
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
  }), [flipbookContext.dimensions.width, flipbookContext.dimensions.height]);

  const wrapSubSlideForFlip = (index) => {
    const slide = subSlides[index];
    if (!slide) return null;
    const bg = resolveSlideBackgroundFromElement(slide, defaultSlideBg, breakpoint);
    return (
      <div key={`sub-flip-${index}`} style={subSlideOuterStyle}>
        <div style={subSlideCardStyle}>
          {/* Offset content below the menu bar (mirrors the static sub-slide card)
              so the fold's top strip stays transparent and the cover's menu bar
              shows through during the animation instead of being overlapped. */}
          <div style={{ position: 'absolute', top: externalBar ? 0 : MENU_BAR_HEIGHT, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
            {bg && <BackgroundViewComponent background={bg} breakpoint={breakpoint} />}
            {slide}
          </div>
        </div>
      </div>
    );
  };

  const subSlideCurrentContent = wrapSubSlideForFlip(subSlideFlip.currentIndex);
  const subSlideUpcomingContent = subSlideFlip.direction
    ? wrapSubSlideForFlip(subSlideFlip.targetIndex)
    : null;

  const handleSubSlideFlipComplete = useCallback(() => {
    isSubFlipAnimatingRef.current = false;
    setSubSlideFlip(s => ({
      currentIndex: s.targetIndex ?? s.currentIndex,
      direction:    null,
      targetIndex:  null,
    }));
  }, []);

  // ── Hyperlink / carousel navigation ─────────────────────────────────────────
  // scrollToSlide: if index 0 → stay on cover; if > 0 → open sub-slides + flip
  const scrollToSlide = useCallback((globalIndex) => {
    if (globalIndex < 0 || globalIndex >= slideWidgetsRef.current.length) return;

    const ctx  = flipbookContextRef.current;

    const useMobileStack = (ctx.experienceBreakpoint ?? ctx.breakpoint) === 'mobile';

    if (globalIndex === 0) {
      // Close sub-slides if open, stay on cover
      if (subSlideOpenRef.current) {
        setSubSlideOpen(false);
        subSlideOpenRef.current = false;
        currentSubSlideRef.current = 0;
        setSubSlideFlip({ currentIndex: 0, direction: null, targetIndex: null });
        if (useMobileStack) setMobileSubSlideIndex(0);
      }
      syncActiveSlideIndex(0);
      ctx.onChange?.('progress', { id: activePageRef.current.id, slideUid: slideWidgetsRef.current[0]?.uid, progress: 100 });
    } else {
      // Open sub-slides and navigate to the right sub-slide
      const subIdx = globalIndex - 1; // convert to 0-based sub-slide index
      if (!subSlideOpenRef.current) {
        setSubSlideOpen(true);
        subSlideOpenRef.current = true;
      }
      currentSubSlideRef.current = subIdx;
      setSubSlideFlip({ currentIndex: subIdx, direction: null, targetIndex: null });
      if (useMobileStack) setMobileSubSlideIndex(subIdx);
      syncActiveSlideIndex(globalIndex);
      ctx.onChange?.('progress', { id: activePageRef.current.id, slideUid: slideWidgetsRef.current[globalIndex]?.uid, progress: 100 });
    }
  }, [setSubSlideOpen, syncActiveSlideIndex]); // eslint-disable-line

  // hyperlink navigation
  useEffect(() => {
    if (!slideDetails.page || loading) return;
    if (slideDetails.page === activePageId && slideDetails.slide && slideMemoIndex > -1) {
      dispatch(updateHyperLinkSlide({ ...slideDetails, slide: '' }));
      return;
    }
    if (slideDetails.page !== activePageId) {
      flipbookContext.navigateToPage(slideDetails.page, fullscreenRef.current, 0);
    }
  }, [slideDetails, loading, fullscreenRef.current, activePageId]);

  useEffect(() => {
    try {
      if (widgetSlideIndex > -1 && slideDetails.page === activePageId && slideDetails.page === page?.id) {
        scrollToSlide(widgetSlideIndex);
        dispatch(updateHyperLinkSlide({ page: '', slide: '', uid: '' }));
      }
    } catch {}
  }, [slideDetails, activePageId, page?.id, slideWidgets]); // eslint-disable-line

  // carousel slide sync
  useEffect(() => {
    if (!carouselSlide?.carouselUid || !Array.isArray(editorWidgets) || !Array.isArray(slideWidgets)) return;
    const widget = editorWidgets.find(d => d.uid === carouselSlide.carouselUid);
    if (!widget) return;
    const slideIndex = slideWidgets.findIndex(d => d.uid === widget.parent);
    const globalIndex = currentSlideIndexRef.current;
    if (slideIndex === globalIndex) return;
    if (slideIndex > -1) scrollToSlide(slideIndex);
  }, [carouselSlide?.carouselUid, editorWidgets, slideWidgets]); // eslint-disable-line

  // Vertical swipe on cover (mobile only). Desktop/tablet: no wheel listener — trackpad scroll must not flip pages (menu / keyboard / controls still navigate).
  let isScrolling = false;
  const isMobileBreakpoint = experienceBreakpoint === 'mobile';

  useEffect(() => {
    const carousel = flipbookPageRef.current;
    if (!carousel || !isMobileBreakpoint) return;
    carousel.addEventListener('touchstart', handleTouchStart, { passive: true  });
    carousel.addEventListener('touchmove',  handleTouchMove,  { passive: false });
    carousel.addEventListener('touchend',   handleTouchEnd,   { passive: true  });
    return () => {
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove',  handleTouchMove);
      carousel.removeEventListener('touchend',   handleTouchEnd);
    };
  }, [isMobileBreakpoint]); // eslint-disable-line react-hooks/exhaustive-deps — handlers stable enough per breakpoint; full deps would re-bind every render

  const didMountRef      = useRef(false);
  const fullScreenClicked= useRef(false);

  useEffect(() => {
    fullscreenRef.current = flipbookContext.fullscreen;
    if (didMountRef.current) {
      fullScreenClicked.current = true;
    } else {
      didMountRef.current = true;
    }
  }, [flipbookContext.fullscreen]);

  useEffect(() => {
    const justBecameActive = flipbookContext.activePage?.id === page?.id &&
      prevActiveIdRef.current !== page?.id;
    prevActiveIdRef.current = flipbookContext.activePage?.id;

    activePageRef.current = flipbookContext.activePage;
    if (flipbookContext.activePage?.id === page?.id) {
      currentSlideIndexRef.current = flipbookContext.activePage?.slideIndex ?? 0;
    }
    if (fullScreenClicked.current) {
      currentSlideIndexRef.current = 0;
      currentSubSlideRef.current = 0;
      setMobileSubSlideIndex(0);
      if (flipbookContext.activePage?.id === page?.id) {
        syncActiveSlideIndex(0);
      }
    }
    const total = slideWidgetsRef.current.length;
    if (total === 1) {
      flipbookContext.onChange('progress', { id: activePageRef.current.id, slideUid: slideWidgetsRef.current[0]?.uid, progress: 100 });
    } else if (justBecameActive && total > 1) {
      flipbookContext.onChange?.('progress', { id: activePageRef.current.id, slideUid: slideWidgetsRef.current[0]?.uid, progress: 100 });
    }
    fullScreenClicked.current = false;
  }, [flipbookContext.activePage, page?.id, syncActiveSlideIndex]);

  // ══════════════════════════════════════════════════════════════════════════════
  // ── COVER TRACKER — page-to-page navigation ─────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  const handleOpenSubSlides = useCallback(() => {
    if (subSlideOpenRef.current || !hasSubSlides) return;
    currentSubSlideRef.current = 0;
    setSubSlideFlip({ currentIndex: 0, direction: null, targetIndex: null });
    setMobileSubSlideIndex(0);
    setSubSlideOpen(true);
    subSlideOpenRef.current = true;
    syncActiveSlideIndex(1);
    flipbookContextRef.current.onChange?.('progress', {
      id: activePageRef.current.id,
      slideUid: slideWidgetsRef.current[1]?.uid,
      progress: 100,
    });
  }, [hasSubSlides, setSubSlideOpen, syncActiveSlideIndex]);

  const handleCloseSubSlides = useCallback(() => {
    if (!subSlideOpenRef.current) return;
    setSubSlideOpen(false);
    subSlideOpenRef.current = false;
    currentSubSlideRef.current = 0;
    setSubSlideFlip({ currentIndex: 0, direction: null, targetIndex: null });
    setMobileSubSlideIndex(0);
    syncActiveSlideIndex(0);
  }, [setSubSlideOpen, syncActiveSlideIndex]);

  const handleNextPage = () => {
    const ctx      = flipbookContextRef.current;
    const allPages = ctx.pages;
    const idx      = allPages.findIndex(p => p.id === activePageRef.current?.id);
    const nextPage = allPages[idx + 1];
    if (nextPage) {
      const navOpts = subSlideOpenRef.current ? { closeDelayMs: 0 } : {};
      ctx.navigateToPage(nextPage.id, fullscreenRef.current, 0, true, navOpts);
    }
  };

  const handlePrevPage = () => {
    const ctx      = flipbookContextRef.current;
    const allPages = ctx.pages;
    const idx      = allPages.findIndex(p => p.id === activePageRef.current?.id);
    const prevPage = allPages[idx - 1];
    if (prevPage) {
      const navOpts = subSlideOpenRef.current ? { closeDelayMs: 0 } : {};
      ctx.navigateToPage(prevPage.id, fullscreenRef.current, 0, true, navOpts);
    }
  };

  // ── register page-level navigation into context (for Menu / keyboard) ───────
  // Only the FlipbookPage that matches context.activePage should register. During
  // mobile slide / desktop fold, multiple page trees mount; the last registration
  // could otherwise be an outgoing page that unmounts and leaves stale next/prev.
  useEffect(() => {
    if (page?.id !== flipbookContext.activePage?.id) return;
    const nav = {
      next: handleNextPage,
      prev: handlePrevPage,
      hasSubSlides,
      toggleSubSlides: () => {
        if (subSlideOpenRef.current) handleCloseSubSlides();
        else handleOpenSubSlides();
      },
    };
    flipbookContextRef.current.registerSlideNavigator?.(nav);
  }, [hasSubSlides, flipbookContext.activePage?.id, page?.id, flipbookContext.gatingActive]); // eslint-disable-line

  // ══════════════════════════════════════════════════════════════════════════════
  // ── SUB-SLIDE TRACKER — fold animation between sub-slides ───────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  const handleNextSubSlide = () => {
    if (isSubFlipAnimatingRef.current) return;
    const current  = currentSubSlideRef.current;
    const total    = subSlides.length;

    if (current < total - 1) {
      const target = current + 1;
      currentSubSlideRef.current = target;

      const globalIndex = target + 1;
      syncActiveSlideIndex(globalIndex);
      flipbookContextRef.current.onChange?.('progress', {
        id: activePageRef.current.id, slideUid: slideWidgetsRef.current[globalIndex]?.uid, progress: 100,
      });

      if (isMobileBreakpoint) {
        isSubFlipAnimatingRef.current = true;
        setMobileSubSlideIndex(target);
        setTimeout(() => { isSubFlipAnimatingRef.current = false; }, MOBILE_SUBSLIDE_STACK_MS);
        return;
      }

      isSubFlipAnimatingRef.current = true;
      setSubSlideFlip({ currentIndex: current, direction: 'next', targetIndex: target });
      return;
    }
    const ctx      = flipbookContextRef.current;
    const allPages = ctx.pages;
    const idx      = allPages.findIndex(p => p.id === activePageRef.current?.id);
    const nextPage = allPages[idx + 1];
    if (nextPage) {
      ctx.navigateToPage(nextPage.id, fullscreenRef.current, 0, true, { closeDelayMs: 0 });
    } else {
      handleCloseSubSlides();
    }
  };

  const handlePrevSubSlide = () => {
    if (isSubFlipAnimatingRef.current) return;
    const current = currentSubSlideRef.current;

    if (current > 0) {
      const target = current - 1;
      currentSubSlideRef.current = target;

      const globalIndex = target + 1;
      syncActiveSlideIndex(globalIndex);
      flipbookContextRef.current.onChange?.('progress', {
        id: activePageRef.current.id, slideUid: slideWidgetsRef.current[globalIndex]?.uid, progress: 100,
      });

      if (isMobileBreakpoint) {
        isSubFlipAnimatingRef.current = true;
        setMobileSubSlideIndex(target);
        setTimeout(() => { isSubFlipAnimatingRef.current = false; }, MOBILE_SUBSLIDE_STACK_MS);
        return;
      }

      isSubFlipAnimatingRef.current = true;
      setSubSlideFlip({ currentIndex: current, direction: 'prev', targetIndex: target });
    }
  };

  // ── scroll / touch navigation (cover card only) ─────────────────────────────
  const handleNavigate = (direction) => {
    if (subSlideOpenRef.current) return; // sub-slides open → button-driven only

    if (direction === 'down') {
      if (hasSubSlides) handleOpenSubSlides();
      else handleNextPage();
    } else {
      handlePrevPage();
    }
  };

  // ── touch handlers (VERTICAL reveal + flip) ───────────────────────────────────

  const handleTouchStart = (event) => {
    startYRef.current = event.touches[0].pageY;
  };

  const handleTouchMove = (event) => {
    event.preventDefault();
    if (isScrolling) return;

    const currentY = event.touches[0].pageY;
    const deltaY   = startYRef.current - currentY;

    if (Math.abs(deltaY) > 50) {
      isScrolling = true;
      handleNavigate(deltaY > 0 ? 'down' : 'up');
      setTimeout(() => { isScrolling = false; }, 1000);
    }
  };

  const handleTouchEnd = () => {
    startYRef.current = 0;
  };

  // ── Sub-slide swipe (mobile breakpoint only): vertical, spatial-style ───────
  const subSlideTouchStartXRef = useRef(0);
  const subSlideTouchStartYRef = useRef(0);
  const subSlideVertSwipeLockRef = useRef(false);
  const subSlideScrollRef = useRef(null);  // mobile vertical-scroll container

  const handleSubSlideTouchStart = useCallback((e) => {
    subSlideTouchStartXRef.current = e.touches[0].clientX;
    subSlideTouchStartYRef.current = e.touches[0].clientY;
  }, []);

  const handleSubSlideTouchEnd = (e) => {
    if (!isMobileBreakpoint) return;
    const deltaX = subSlideTouchStartXRef.current - e.changedTouches[0].clientX;
    const deltaY = subSlideTouchStartYRef.current - e.changedTouches[0].clientY;
    const threshold = 50;

    // Swipe DOWN while at the very top of the sub-slide scroll → return to the cover
    // (mirrors the cover's swipe-UP → open sub-slides). deltaY < 0 means the finger
    // moved down; guarded to a clear vertical swipe at scrollTop 0.
    const atTop = (subSlideScrollRef.current?.scrollTop || 0) <= 2;
    const strongVertDown = deltaY < -threshold && Math.abs(deltaY) > Math.abs(deltaX);
    if (atTop && strongVertDown && !subSlideVertSwipeLockRef.current) {
      subSlideVertSwipeLockRef.current = true;
      handleCloseSubSlides();
      setTimeout(() => { subSlideVertSwipeLockRef.current = false; }, 1000);
      return;
    }

    // Vertical sub-slide navigation is handled by NATIVE SCROLL now. This only keeps
    // the horizontal page change (clear horizontal swipe at the first/last sub-slide).
    const ctx      = flipbookContextRef.current;
    const allPages = ctx.pages;
    const thisIdx  = allPages.findIndex(p => p.id === activePageRef.current?.id);
    const curSub   = currentSubSlideRef.current;
    const lastSub  = subSlides.length - 1;
    const strongHoriz = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= threshold;
    const canHorizNext = deltaX > 0 && curSub === lastSub && thisIdx < allPages.length - 1;
    const canHorizPrev = deltaX < 0 && curSub === 0 && thisIdx > 0;

    if (strongHoriz && (canHorizNext || canHorizPrev) && !subSlideVertSwipeLockRef.current) {
      subSlideVertSwipeLockRef.current = true;
      if (canHorizNext) {
        ctx.navigateToPage(allPages[thisIdx + 1].id, fullscreenRef.current, 0, true, { closeDelayMs: 0 });
      } else {
        ctx.navigateToPage(allPages[thisIdx - 1].id, fullscreenRef.current, 0, true, { closeDelayMs: 0 });
      }
      setTimeout(() => { subSlideVertSwipeLockRef.current = false; }, 1000);
    }
  };

  // ── Arrow disabled logic ─────────────────────────────────────────────────────
  const allPages    = flipbookContext.pages || [];
  const visiblePages = useMemo(() => allPages.filter(p => !p.hidden), [allPages]);
  const thisPageIdx = allPages.findIndex(p => p.id === page?.id);
  const visiblePageIdx = visiblePages.findIndex(p => p.id === page?.id);
  const totalPages = visiblePages.length;
  const currentPageNum = visiblePageIdx >= 0 ? visiblePageIdx + 1 : 0;
  const isFirstPage = thisPageIdx === 0;
  const isLastPage  = thisPageIdx === allPages.length - 1;

  // Cover card: prev/next are page-level
  const isCoverPrevDisabled = isFirstPage;
  const isCoverNextDisabled = isLastPage;

  // Sub-slide card: prev/next are sub-slide-level
  const isSubPrevDisabled = isMobileBreakpoint ? mobileSubSlideIndex <= 0 : currentSubSlideRef.current <= 0;
  const isSubNextDisabled = isLastPage
    && (isMobileBreakpoint ? mobileSubSlideIndex >= subSlides.length - 1 : currentSubSlideRef.current >= subSlides.length - 1);
  // On the last sub-slide, "next" flips to the next page — show a double chevron to signal that.
  const isOnLastSubSlide = isMobileBreakpoint
    ? mobileSubSlideIndex >= subSlides.length - 1
    : currentSubSlideRef.current >= subSlides.length - 1;

  // Reset overlay on page change (skip initial mount — flip slot mounts would close the overlay)
  const prevPageIdRef = useRef(flipbookContext.activePage?.id);
  useEffect(() => {
    if (prevPageIdRef.current === flipbookContext.activePage?.id) return; // same page, skip
    prevPageIdRef.current = flipbookContext.activePage?.id;
    setSubSlideOpen(false);
    subSlideOpenRef.current = false;
    currentSubSlideRef.current = 0;
    currentSlideIndexRef.current = 0;
    setSubSlideFlip({ currentIndex: 0, direction: null, targetIndex: null });
    setMobileSubSlideIndex(0);
  }, [flipbookContext.activePage?.id]);

  const subSlidePaneH = flipbookContext.dimensions.height || 0;
  // Mobile continuous scroll: each sub-slide pane is AUTO height (its own content
  // height) so the stack has variable-height panes. Scroll bookkeeping reads the real
  // DOM position (offsetTop) of each pane instead of a fixed per-pane height.
  const subSlidePaneRefs = useRef([]);
  const lastSubScrollTopRef = useRef(0);

  // Mobile: free native scroll. Track which sub-slide occupies the viewport middle to
  // keep the active index + progress in sync (variable heights → measured offsets).
  const handleSubSlideScroll = useCallback((e) => {
    if (!isMobileBreakpoint) return;
    // Pill shrinks ONLY at the bottom of the content; the white fade shows ONLY while
    // scrolling UP toward the top (hidden while scrolling down / at the bottom). Set
    // before the early return below so it tracks even when the active index is unchanged.
    const el = e.target;
    const st = el.scrollTop;
    const atBottom = st + el.clientHeight >= el.scrollHeight - 8;
    const prev = lastSubScrollTopRef.current;
    // Sticky: the last scroll direction decides the pill size and PERSISTS on idle;
    // reaching the bottom of the content forces it back to normal.
    let dir;
    if (st > prev + 2) { dir = 'down'; lastSubScrollTopRef.current = st; }
    else if (st < prev - 2) { dir = 'up'; lastSubScrollTopRef.current = st; }
    flipbookContextRef.current.setSubSlideScroll?.({ dir, atBottom });
    const mid = st + (el.clientHeight * 0.5);
    const panes = subSlidePaneRefs.current;
    let idx = 0;
    for (let i = 0; i < subSlides.length; i++) {
      if (panes[i] && panes[i].offsetTop <= mid) idx = i;
      else if (panes[i]) break;
    }
    idx = Math.max(0, Math.min(idx, subSlides.length - 1));
    if (idx === currentSubSlideRef.current) return;
    currentSubSlideRef.current = idx;
    const globalIndex = idx + 1;            // 0 = cover; sub-slides start at 1
    syncActiveSlideIndex(globalIndex);
    flipbookContextRef.current.onChange?.('progress', {
      id: activePageRef.current.id, slideUid: slideWidgetsRef.current[globalIndex]?.uid, progress: 100,
    });
  }, [isMobileBreakpoint, subSlides.length, syncActiveSlideIndex]);

  // Mobile: scroll the stack to a programmatically-requested sub-slide (open / hyperlink / carousel).
  useEffect(() => {
    if (!isMobileBreakpoint) return;
    const el = subSlideScrollRef.current;
    const pane = subSlidePaneRefs.current[mobileSubSlideIndex];
    if (!el || !pane) return;
    const targetTop = pane.offsetTop;
    if (Math.abs(el.scrollTop - targetTop) > 2) {
      el.scrollTo({ top: targetTop, behavior: 'auto' });
    }
  }, [mobileSubSlideIndex, subSlideOpen, isMobileBreakpoint]); // eslint-disable-line

  // Reveal the arrow for whichever half the cursor is in. excludeBar=true ignores
  // the top menu-bar strip (cover card) so hovering the bar doesn't show an arrow.
  const trackHalf = (setter, excludeBar = false) => (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (excludeBar && (e.clientY - rect.top) < MENU_BAR_HEIGHT) { setter(null); return; }
    const side = (e.clientX - rect.left) < rect.width / 2 ? 'left' : 'right';
    setter(prev => (prev === side ? prev : side));
  };

  const trackCoverHover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const x = e.clientX - rect.left;

    if (y < MENU_BAR_HEIGHT) setCoverArrowSide(null);
    else {
      const side = x < rect.width / 2 ? 'left' : 'right';
      setCoverArrowSide(prev => (prev === side ? prev : side));
    }

    const atBottom = y > rect.height - BOTTOM_HOVER_ZONE;
    setCoverBottomHover(prev => (prev === atBottom ? prev : atBottom));
  };

  const handleCoverMouseLeave = () => {
    setCoverArrowSide(null);
    setCoverBottomHover(false);
  };

  return (
    <motion.div
      initial={{ opacity: 1 }}
      id={page && page.id}
      className={styles.flipbookPage}
    >
      {/* ── Wrapper keeps all layers aligned to card dimensions ── */}
      <div
        className={styles.flipbookPageWrapper}
        style={{
          width:  `${flipbookContext.dimensions.width}px`,
          height: `${flipbookContext.dimensions.height}px`,
        }}
      >
        {/* ══ COVER CARD (slide 1 only) ══ */}
        <div
          id={`cs-flipbook-page-${page?.id}`}
          className={cx(styles.flipbookPageInner, 'flipbookContainer',
            { [styles.flipbookPageInnerHidden]: subSlideOpen && isMobileBreakpoint && hasSubSlides },
            { [styles.flipbookPageInnerFull]: externalBar },
            { [styles.highlighted]: flipbookContext.showHighlight },
            { [styles.fullscreen]:  flipbookContext.fullscreen }
          )}
          onMouseMove={trackCoverHover}
          onMouseLeave={handleCoverMouseLeave}
        >
          {loading && <LoadingMessage />}
          {coverBackground && <BackgroundViewComponent background={coverBackground} breakpoint={breakpoint} />}

          <div
            id='cs-flipbook-slide'
            ref={flipbookPageRef}
            className={cx(styles.flipbookPageContainer)}
            style={externalBar
              ? { marginTop: 0, height: '100%' }
              : { marginTop: MENU_BAR_HEIGHT, height: `calc(100% - ${MENU_BAR_HEIGHT}px)` }}
          >
            {coverSlide}
          </div>

          {/* ══ Top menu bar — landscape only (lives inside the card, folds with the page).
               Mobile / iPad portrait use the portaled bar at the viewport top instead. ══ */}
          {!externalBar && (
          <div className={styles.flipbookTopBar} style={{ height: `${MENU_BAR_HEIGHT}px` }}>
            <div className={styles.menuBarLeft}>
              <button
                className={styles.menuButton}
                type="button"
                aria-label="Menu"
                disabled={!!flipbookContext.gatingActive}
                onClick={() => flipbookContext.setMenuPanelOpen?.(!flipbookContext.menuPanelOpen)}
              >
                <i className="cleverstory-flip-menu" />
                {!(flipbookContext.subSlideOpen && experienceBreakpoint !== 'mobile') && (
                  <span className={styles.title}>Content</span>
                )}
              </button>

              {/* Desktop only: fade-in "Back to <page>" while the sub-slide is open */}
              {experienceBreakpoint !== 'mobile' && (
                <div className={cx(styles.backGroup, { [styles.backGroupVisible]: flipbookContext.subSlideOpen && !flipbookContext.gatingActive })}>
                  <span className={styles.menuBarSeparator} />
                  <button
                    className={styles.backButton}
                    type="button"
                    aria-label="Back"
                    onClick={handleCloseSubSlides}
                  >
                    <i className="cleverstory-drop-down-left" />
                    <span className={styles.title}>Back to  <TruncatedTextWithTooltip content={page?.name} className={styles.label} maxWidth={300} placement='bottom'/>
                    </span>
                  </button>
                </div>
              )}
            </div>

            <button
              className={styles.menuButton}
              type="button"
              aria-label="Expand"
              onClick={() => flipbookContext.onToggleFullscreen?.()}
            >
              <i className="cleverstory-expand-editor" />
            </button>
          </div>
          )}

          {/* Cover card nav buttons */}
          {!loading && !flipbookContext.gatingActive && (
            <div className={styles.cardNavControls}>
              <button
                className={cx(styles.pageNavArrow, styles.pageNavArrowPrev, {
                  [styles.pageNavArrowDisabled]: isCoverPrevDisabled,
                  [styles.pageNavArrowHidden]:   coverArrowSide !== 'left',
                })}
                onClick={handlePrevPage}
                disabled={isCoverPrevDisabled}
                aria-label="Previous page"
              >
                <i className="cleverstory-drop-down-left" />
              </button>

              <button
                className={cx(styles.pageNavArrow, styles.pageNavArrowNext, {
                  [styles.pageNavArrowDisabled]: isCoverNextDisabled,
                  [styles.pageNavArrowHidden]:   coverArrowSide !== 'right',
                })}
                onClick={handleNextPage}
                disabled={isCoverNextDisabled}
                aria-label="Next page"
              >
                <i className="cleverstory-drop-down-right" />
              </button>

              {/* {(hasSubSlides && breakpoint !== 'mobile' ) && (
                <button
                  className={cx(styles.dropdownArrow)}
                  onClick={handleOpenSubSlides}
                  aria-label="Open sub-slides"
                >
                  <i className="cleverstory-drop-down" />
                </button>
              )} */}
            </div>
          )}

          {!externalBar && !loading && !flipbookContext.gatingActive && totalPages > 0 && (
            <div
              className={cx(styles.cardPageIndicator, {
                [styles.cardPageIndicatorVisible]: coverBottomHover,
              })}
              aria-live="polite"
            >
              Page {currentPageNum}/{totalPages}
            </div>
          )}
        </div>
      </div>

      {/* ══ SUB-SLIDE CARD (at page level — slides in as a separate card) ══ */}
      {hasSubSlides && (
        <div
          className={cx(styles.subSlideCard, {
            [styles.subSlideCardVisible]: subSlideOpen,
            [styles.subSlideCardSquareTop]: !externalBar,
            [styles.subSlideCardFull]: externalBar,
          })}
          style={externalBar ? {
            // Mobile / iPad portrait: bar is external → full card (no in-card bar offset).
            // Explicit top/left match the cover card's centering so hit-testing is consistent
            // across browsers (position:absolute flex children with no top/left are browser-defined).
            width:  `${flipbookContext.dimensions.width}px`,
            height: `${flipbookContext.dimensions.height}px`,
            top:    `calc((100% - ${flipbookContext.dimensions.height}px) / 2)`,
            left:   `calc((100% - ${flipbookContext.dimensions.width}px) / 2)`,
          } : {
            // Landscape: sit BELOW the in-card menu bar so it stays visible AND clickable
            // (the card box no longer overlaps the top MENU_BAR_HEIGHT strip).
            width:  `${flipbookContext.dimensions.width}px`,
            height: `${flipbookContext.dimensions.height - MENU_BAR_HEIGHT}px`,
            top:  `calc((100% - ${flipbookContext.dimensions.height}px) / 2 + ${MENU_BAR_HEIGHT}px)`,
            left: `calc((100% - ${flipbookContext.dimensions.width}px) / 2)`,
          }}
          onMouseMove={trackHalf(setSubArrowSide)}
          onMouseLeave={() => setSubArrowSide(null)}
        >
          {/* Inner wrapper provides border-radius + overflow clipping WITHOUT a transform.
              Keeping overflow:hidden off the outer .subSlideCard (which has a CSS transform
              for the slide-in animation) fixes iOS Safari's known bug where a transformed
              overflow:hidden parent blocks scroll in descendant scroll containers. */}
          <div className={cx(styles.subSlideCardInner, { [styles.subSlideCardInnerFull]: externalBar })}>
            {isMobileBreakpoint ? (
              <div
                ref={subSlideScrollRef}
                className={styles.subSlideVerticalViewport}
                onScroll={handleSubSlideScroll}
                onTouchStart={handleSubSlideTouchStart}
                onTouchEnd={handleSubSlideTouchEnd}
              >
                <div
                  className={styles.subSlideVerticalTrack}
                  style={{ height: subSlides.length === 1 ? '100%' : undefined }}
                >
                  {subSlides.map((slide, i) => {
                    const bg = resolveSlideBackgroundFromElement(slide, defaultSlideBg);
                    const single = subSlides.length === 1;
                    // The slide paints scaled (transform: scale), but transform doesn't
                    // affect layout flow — so wrap it in a layer that RESERVES the scaled
                    // height (naturalHeight × scale, origin top). The auto-height pane then
                    // measures the true painted height and slides stack with no overlap/gap.
                    const rawH = get(slideWidgets[i + 1], 'properties.mobile.layout.height');
                    const natH = typeof rawH === 'number' ? rawH : parseFloat(rawH);
                    const reservedH = (!isNaN(natH) && natH > 0)
                      ? natH * (flipbookContext.scale || 1)
                      : null;
                    return (
                      <div
                        key={`mob-sub-${page?.id}-${i}`}
                        ref={(el) => { subSlidePaneRefs.current[i] = el; }}
                        className={styles.subSlideVerticalPane}
                        // Single sub-slide: fill the card (no scroll). Multiple: auto
                        // height each, stacked in the continuous scroll.
                        style={{ height: single ? '100%' : undefined }}
                      >
                        {bg && <BackgroundViewComponent background={bg} breakpoint={breakpoint} />}
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            height: single ? '100%' : (reservedH ? `${reservedH}px` : '100%'),
                            transformOrigin: 'top',
                            overflow: 'hidden',
                          }}
                        >
                          {slide}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', visibility: subSlideFlip.direction ? 'hidden' : 'visible' }}>
                {idleSubSlideBackground && (
                  <BackgroundViewComponent background={idleSubSlideBackground} breakpoint={breakpoint} />
                )}
                {subSlides[subSlideFlip.currentIndex]}
              </div>
            )}

            {/* Sub-slide card nav buttons */}
            {!flipbookContext.gatingActive && <div className={styles.cardNavControls}>
              <button
                className={cx(styles.pageNavArrow, styles.pageNavArrowPrev, {
                  [styles.pageNavArrowDisabled]: isSubPrevDisabled,
                  [styles.pageNavArrowHidden]:   subArrowSide !== 'left',
                })}
                onClick={handlePrevSubSlide}
                disabled={isSubPrevDisabled}
                aria-label="Previous sub-slide"
              >
                <i className="cleverstory-drop-down-left" />
              </button>

              <button
                className={cx(styles.pageNavArrow, styles.pageNavArrowNext, {
                  [styles.pageNavArrowDisabled]: isSubNextDisabled,
                  [styles.pageNavArrowHidden]:   subArrowSide !== 'right',
                })}
                onClick={handleNextSubSlide}
                disabled={isSubNextDisabled}
                aria-label={isOnLastSubSlide ? 'Next page' : 'Next sub-slide'}
              >
                {isOnLastSubSlide ? (
                  <span className={styles.doubleChevron}>
                    <i className="cleverstory-drop-down-right" />
                    <i className="cleverstory-drop-down-right" />
                  </span>
                ) : (
                  <i className="cleverstory-drop-down-right" />
                )}
              </button>

            {/* {  breakpoint !== 'mobile' &&
              <button
                className={cx(styles.dropdownArrow, styles.dropdownArrowOpen)}
                onClick={handleCloseSubSlides}
                aria-label="Close sub-slides"
              >
                <i className="cleverstory-drop-down" />
              </button>
            } */}
            </div>}
          </div>
        </div>
      )}

      {/* ══ SUB-SLIDE FLIP OVERLAY (at page level, only during animation) ══
          Rendered as sibling of .flipbookPageWrapper so .flipViewport fills
          the full .flipbookPage (100vw × 100%) — matching page-level flip.
          Each sub-slide is wrapped as a centered card, giving the fold
          breathing room and depth identical to page-to-page flip. */}
      {!isMobileBreakpoint && subSlideFlip.direction && !flipbookContext.flipDirection && (
        <FlipbookFlipContainer
          currentContent={subSlideCurrentContent}
          upcomingContent={subSlideUpcomingContent}
          direction={subSlideFlip.direction}
          onAnimationEnd={handleSubSlideFlipComplete}
          mobile={isMobileBreakpoint}
        />
      )}
    </motion.div>
  );
});

// tiny helper so we don't duplicate React.useContext calls
function useContext_safe(Context) {
  return React.useContext(Context);
}

FlipbookContent.FlipbookPage = FlipbookPage;

// ─── Loading indicator ────────────────────────────────────────────────────────

const LoadingMessage = () => (
  <div className={styles.loadingMessage}>
    <span className={styles.loadingDot} />
    <span className={styles.loadingDot} />
    <span className={styles.loadingDot} />
  </div>
);

// ─── Menu (mobile + iPad portrait only) ──────────────────────────────────────

// Bottom-centered nav for the external-bar layouts (mobile + iPad portrait):
// a glass pill [‹ Page X/Y ›] plus a separate round ⌄ button that toggles the
// active page's sub-slides. Arrows are PAGE-LEVEL (flip prev/next page; sub-slide
// stepping is handled by the in-card arrows). Rendered by FlipbookContent only
// when externalBar is true. Position comes from the measured `menuPosition`.

const Menu = React.memo(({ menuPosition, activePage, pages, navigateToPage }) => {
  const flipbookContext = React.useContext(FlipbookContext);
  const gatingActive = !!flipbookContext.gatingActive;
  const hasSubSlides = !!flipbookContext.activePageHasSubSlides;
  const subSlideOpen = !!flipbookContext.subSlideOpen;
  // Small while reading (sub-slides open, not at the bottom, not scrolling up); returns
  // to full size at the bottom or while scrolling up toward the top.
  // Sticky: small while the last scroll was DOWN; normal on UP / at the bottom / cover.
  const compact = subSlideOpen
    && flipbookContext.subSlideScrollDir === 'down'
    && !flipbookContext.subSlideAtBottom;

  // Compact keeps the faded dropdown in its slot (so it stays static), which would push
  // the small pill left of center. Nudge the whole Menu right by half the dropdown+gap
  // width (48 + 14) / 2 = 31px so the small pill lands at true center. Only when the
  // dropdown is actually present. Animates via the .flipbookNav transform transition.
  const compactRecenter = compact && hasSubSlides ? 31 : 0;
  const navTransform = `translate(-50%, -100%) translateX(${compactRecenter}px)`;

  const [show, setShow] = useState(false);
  const prevGatingActiveRef = useRef(undefined);

  // Reveal once the position math has placed the menu (menuPosition.y set).
  useEffect(() => {
    if (menuPosition.y === 0) return;
    const t = setTimeout(() => setShow(true), 1000);
    return () => clearTimeout(t);
  }, [menuPosition.y]);

  // Hide while the gating screen is up; restore when it closes.
  useEffect(() => {
    if (gatingActive) setShow(false);
    else if (prevGatingActiveRef.current === true) setShow(true);
    prevGatingActiveRef.current = gatingActive;
  }, [gatingActive]);

  const visiblePages = useMemo(() => (pages || []).filter(p => !p.hidden), [pages]);
  const activeIndex  = visiblePages.findIndex(p => p.id === activePage.id);
  const total        = visiblePages.length;
  const isFirst      = activeIndex <= 0;
  const isLast       = activeIndex < 0 || activeIndex >= total - 1;

  // Page-level navigation (flip prev/next page; ignores sub-slides).
  const handlePrev = () => { if (!isFirst) navigateToPage(visiblePages[activeIndex - 1].id, false, 0, true); };
  const handleNext = () => { if (!isLast)  navigateToPage(visiblePages[activeIndex + 1].id, false, 0, true); };

  return (
    <div
      id="cs-flipbook-menu"
      style={{ left: `${menuPosition.x}px`, top: `${menuPosition.y}px`, transform: navTransform }}
      className={cx(styles.flipbookNav, {
        [styles.flipbookNavShow]:    show,
        [styles.flipbookNavHidden]:  gatingActive,
        [styles.flipbookNavCompact]: compact,
      })}
    >
      <div className={styles.flipbookNavPill}>
        <button
          className={styles.flipbookNavArrow}
          onClick={handlePrev}
          disabled={isFirst}
          aria-label="Previous page"
        >
          <span className={styles.doubleChevron}>
            <i className="cleverstory-flip-left" />
          </span>
        </button>

        <span className={styles.flipbookNavLabel}>{total ? activeIndex + 1 : 0}/{total}</span>

        <button
          className={styles.flipbookNavArrow}
          onClick={handleNext}
          disabled={isLast}
          aria-label="Next page"
        >
          <span className={styles.doubleChevron}>
            <i className="cleverstory-flip-right" />
          </span>
        </button>
      </div>

      {hasSubSlides && (
        <button
          className={cx(styles.flipbookNavDropdown, { [styles.flipbookNavDropdownOpen]: subSlideOpen })}
          onClick={() => flipbookContext.toggleSubSlidesAction?.()}
          aria-label="Toggle sub-slides"
        >
          <i className="cleverstory-flip-arrow" />
        </button>
      )}
    </div>
  );
});

export function getColorStyles(value) {
  if (!value) return null;
  const color = `rgba(${value.r}, ${value.g}, ${value.b}, ${value.a})`;
  return { color };
}

// ─── useViewportSize ──────────────────────────────────────────────────────────

function useViewportSize() {
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0, viewportHeight: '100vh' });
  const [mounted, setMounted]           = useState(false);

  useEffect(() => {
    setMounted(true);
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      const vh = (window.visualViewport ? window.visualViewport.height : window.innerHeight) * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.body.style.height = `${vh * 100}px`;
      setViewportSize({
        width:          window.visualViewport?.width  || window.innerWidth,
        height:         window.visualViewport?.height || window.innerHeight,
        viewportHeight: `${vh * 100}px`,
      });
    };
    handleResize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  if (!mounted) return { width: 1200, height: 800, viewportHeight: '100vh' };
  return viewportSize;
}
